import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchClergyBySlug } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import SeoHead from '@/components/Seo';

interface Parish {
    name: string;
    slug: string;
    location?: string;
}

interface Archdeaconry {
    name: string;
    slug: string;
}

interface ClergyMember {
    id: number;
    title: string;
    name: string;
    slug: string;
    dob?: string | null;
    wedding?: string | null;
    photo?: string;
    hierarchy?: number;
    qualification?: string;
    hobby?: string;
    passion?: string;
    email?: string;
    phone?: string;
    biography?: string;
    parish?: Parish;
    archdeaconry?: Archdeaconry;
}

const ClergyDetail = () => {
    const { slug } = useParams<{ slug: string }>();
    const [clergy, setClergy] = useState<ClergyMember | null>(null);
    const [loading, setLoading] = useState(true);
    const { toast } = useToast();

    useEffect(() => {
        const loadClergy = async () => {
            try {
                const response = await fetchClergyBySlug(slug!);
                const item = response.data;

                const transformed: ClergyMember = {
                    id: item.id,
                    title: item.title,
                    name: item.name,
                    slug: item.slug,
                    dob: item.dob,
                    wedding: item.wedding,
                    photo: item.photo,
                    hierarchy: item.hierarchy,
                    qualification: item.qualification,
                    hobby: item.hobby,
                    passion: item.passion,
                    email: item.email,
                    phone: item.phone,
                    biography: item.biography,
                    parish: item.parish,
                    archdeaconry: item.archdeaconry,
                };

                setClergy(transformed);
            } catch (error) {
                console.error('Error fetching clergy:', error);
                toast({
                    title: 'Error',
                    description: 'Failed to fetch clergy details.',
                    variant: 'destructive',
                });
            } finally {
                setLoading(false);
            }
        };

        loadClergy();
    }, [slug, toast]);

    if (loading) return <div className="p-10 text-center text-gray-500 text-lg">Loading...</div>;
    if (!clergy) return <div className="p-10 text-center text-red-500 text-lg">Clergy not found.</div>;

    return (
        <div className="max-w-5xl mx-auto px-4 py-10">
            <SeoHead
                title={`${clergy.title} ${clergy.name}  | Ibadan South Diocese`}
                description="Explore the ministers and parishes of our Diocese."
                ogImage={clergy.photo}
                url={`https://ibadansouthanglicandiocese.org.ng/${clergy.slug}`}
            />

{/* https://res.cloudinary.com/dwrqyrm84/image/upload/v1750874183/home_ss1tdy.jpg */}

            <Card className="shadow-lg border border-gray-200 rounded-2xl overflow-hidden">
                <div className="md:flex">
                    <div className="md:w-1/3 bg-gray-100 flex items-start justify-center p-6">
                        <img
                            src={clergy.photo}
                            alt={clergy.name}
                            className="w-full h-auto rounded-lg object-cover"
                        />
                    </div>

                    <div className="md:w-2/3 p-6 space-y-4">
                        <CardHeader className="p-0 mb-3">
                            <CardTitle className="text-3xl font-semibold text-gray-800">
                                {clergy.title} {clergy.name}
                            </CardTitle>
                            {/* <p className="text-sm text-gray-500">Hierarchy Level: {clergy.hierarchy ?? 'N/A'}</p> */}
                        </CardHeader>

                        <CardContent className="p-0 space-y-3 text-gray-800 text-[15px]">
                            {clergy.qualification && (
                                <p>
                                    <strong>Qualifications:</strong> {clergy.qualification}
                                </p>
                            )}
                            {clergy.hobby && (
                                <p>
                                    <strong>Hobbies:</strong> {clergy.hobby}
                                </p>
                            )}
                            {clergy.passion && (
                                <p>
                                    <strong>Passions:</strong> {clergy.passion}
                                </p>
                            )}
                            {clergy.email && (
                                <p>
                                    <strong>Email:</strong>{' '}
                                    <Link to={`mailto:${clergy.email}`} className="text-blue-600 underline">
                                        {clergy.email}
                                    </Link>
                                </p>
                            )}
                            {clergy.phone && (
                                <p>
                                    <strong>Phone:</strong> {clergy.phone}
                                </p>
                            )}
                            {clergy.dob && (
                                <p>
                                    <strong>Date of Birth:</strong> {clergy.dob}
                                </p>
                            )}
                            {clergy.wedding && (
                                <p>
                                    <strong>Wedding Date:</strong> {clergy.wedding}
                                </p>
                            )}
                            {clergy.parish && (
                                <p>
                                    <strong>Parish:</strong> {clergy.parish.name}
                                </p>
                            )}
                            {clergy.archdeaconry && (
                                <p>
                                    <strong>Archdeaconry:</strong> {clergy.archdeaconry.name}
                                </p>
                            )}
                            {clergy.biography && (
                                <div>
                                    <strong>Biography:</strong>
                                    <div
                                        className="whitespace-pre-wrap mt-1 text-gray-700"
                                        dangerouslySetInnerHTML={{ __html: clergy.biography.replace(/\r?\n/g, '<br/>') }}
                                    />
                                </div>
                            )}
                        </CardContent>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default ClergyDetail;
